// Copyright (c) 2025 UXtechLab.
// Originally created by @taekie. Released under the MIT License. See LICENSE for details.
/**
 * PopupHelpers
 *
 * Helper functions for creating popup displays when cells are clicked.
 * These are optional utilities that can be used with the onClick option.
 */

/**
 * Default popup function for Observable notebooks
 * Returns an HTML element that displays information about the clicked cell
 *
 * @param {Object} clickedData - The data object passed from the click event
 * @param {Object} clickedData.data - The data associated with the clicked cell
 * @param {Event} clickedData.event - The original click event
 * @returns {HTMLElement|null} HTML element for Observable to display, or null if no data
 *
 * @example
 * // In Observable notebook
 * import { VoronoiBubble, showVoronoiPopup } from "..."
 *
 * chart = {
 *   const bubble = new VoronoiBubble();
 *   return bubble.render(data, {
 *     onClick: showVoronoiPopup
 *   });
 * }
 */
export function showVoronoiPopup(clickedData) {
  if (!clickedData) return null;

  const data = clickedData.data || {};

  // This uses Observable's html template literal
  // For non-Observable environments, use createDOMPopup instead
  if (typeof html !== 'undefined') {
    return html`<div style="
      background: #fffe;
      border: 2px solid #555;
      border-radius: 15px;
      padding: 15px;
      max-width: 350px;
      min-width: 200px;
    ">
      <div style="font-size: 1.2em; font-weight: bold; margin-bottom: 0.5em;">
        ${data.subgroup || 'N/A'}
      </div>
      <div style="margin-bottom: 0.3em;">
        <strong>Region:</strong> ${data.group || 'N/A'}
      </div>
      <div>
        <strong>Size:</strong> ${data.size || 'N/A'}
      </div>
    </div>`;
  }

  // Fallback for non-Observable environments
  return createDOMPopup(clickedData);
}

/**
 * Create a DOM-based popup for standard web pages (non-Observable)
 * This creates an absolutely positioned popup at the click location
 *
 * @param {Object} clickedData - The data object passed from the click event
 * @param {Object} clickedData.data - The data associated with the clicked cell
 * @param {Event} clickedData.event - The original click event
 * @returns {HTMLElement|null} DOM element to be appended to the page
 *
 * @example
 * // In standard HTML/JavaScript
 * const bubble = new VoronoiBubble();
 * const svg = bubble.render(data, {
 *   onClick: createDOMPopup
 * });
 */
export function createDOMPopup(clickedData) {
  // Remove existing popup
  const existingPopup = document.querySelector('.vb-popup-content');
  if (existingPopup) existingPopup.remove();

  if (!clickedData) {
    return null;
  }

  const event = clickedData.event;
  const data = clickedData.data || {};

  // Create popup
  const popup = document.createElement('div');
  popup.className = 'vb-popup-content';

  // Position at click location
  const x = event.pageX;
  const y = event.pageY;
  popup.style.left = x + 'px';
  popup.style.top = y + 'px';

  // Create popup content
  const content = document.createElement('div');
  content.className = 'vb-popup-message';
  content.innerHTML = `
    <div style="font-size: 1.2em; font-weight: bold; margin-bottom: 0.5em;">
      ${data.subgroup || 'N/A'}
    </div>
    ${data.item ? `<div style="margin-bottom: 0.5em; color: #555;">${data.item}</div>` : ''}
    <div style="margin-bottom: 0.3em;">
      <strong>Region:</strong> ${data.group || 'N/A'}
    </div>
    <div>
      <strong>Size:</strong> ${data.size || 'N/A'}
    </div>
  `;

  popup.appendChild(content);
  document.body.appendChild(popup);

  // Close on click outside
  setTimeout(() => {
    const closeHandler = (e) => {
      if (!popup.contains(e.target)) {
        popup.remove();
        document.removeEventListener('click', closeHandler);
      }
    };
    document.addEventListener('click', closeHandler);
  }, 0);

  return popup;
}

/**
 * Get the recommended CSS styles for popups
 * Returns a string of CSS that can be added to your page
 *
 * @returns {string} CSS string for popup styles
 *
 * @example
 * // In Observable
 * html`<style>${getPopupStyles()}</style>`
 *
 * @example
 * // In standard HTML
 * const style = document.createElement('style');
 * style.textContent = getPopupStyles();
 * document.head.appendChild(style);
 */
export function getPopupStyles() {
  return `
.vb-popup-content {
  position: absolute;
  background: #fffe;
  border: 2px solid #555;
  border-radius: 30px;
  padding: 10px;
  z-index: 1000000;
  transform: translateX(-50%) translateY(-100%);
  min-width: 100px;
}

.vb-popup-content::before {
  content: " ";
  position: absolute;
  top: 100%;
  left: 50%;
  margin-left: -10px;
  border-width: 10px;
  border-style: solid;
  border-color: #555 transparent transparent transparent;
}

.vb-popup-content::after {
  content: " ";
  position: absolute;
  top: 100%;
  left: 50%;
  margin-left: -8px;
  border-width: 8px;
  border-style: solid;
  border-color: #fff transparent transparent transparent;
}

/* Primary popup (showVoronoiPopup from utils): the JS already positions the
   .vb-popup at the final top-left, so the inner content must NOT
   re-apply the legacy centering transform — otherwise it double-offsets up-left.
   Scoped to .vb-popup so the legacy popup keeps its transform. */
.vb-popup .vb-popup-content {
  position: relative;
  transform: none;
}
/* When placed below the cell, flip the arrow to point up from the top edge. */
.vb-popup.vb-popup-below .vb-popup-content::before {
  top: auto;
  bottom: 100%;
  border-color: transparent transparent #555 transparent;
}
.vb-popup.vb-popup-below .vb-popup-content::after {
  top: auto;
  bottom: 100%;
  border-color: transparent transparent #fff transparent;
}

.vb-popup-message {
  max-width: 350px;
  min-width: 200px;
  padding: 1em;
  line-height: 1.5;
  color: #444;
  text-align: left;
  max-height: 400px;
  overflow-y: scroll;
  overflow-x: clip;
}
`;
}

/**
 * Get comprehensive CSS styles for bubble/voronoi visualizations
 * Returns a string of CSS including fonts, regions, areas, labels, and popups
 *
 * @returns {string} CSS string for all bubble styles
 *
 * @example
 * // In Observable
 * html`<style>${getBubbleStyles()}</style>`
 *
 * @example
 * // In standard HTML
 * const style = document.createElement('style');
 * style.textContent = getBubbleStyles();
 * document.head.appendChild(style);
 */
export function getBubbleStyles() {
  return `
@font-face {
    font-family: 'KoddiUD OnGothic';
    src: url('https://fastly.jsdelivr.net/gh/projectnoonnu/noonfonts_2105_2@1.0/KoddiUDOnGothic-Regular.woff') format('woff');
    font-weight: normal;
    font-style: normal;
}

@font-face {
    font-family: 'KoddiUDOnGothic-Bold';
    src: url('https://fastly.jsdelivr.net/gh/projectnoonnu/noonfonts_2105_2@1.0/KoddiUDOnGothic-Bold.woff') format('woff');
    font-weight: normal;
    font-style: normal;
}

.vb-chart, .vb-popup {
    font-family: "KoddiUD OnGothic", sans-serif;
}

.vb-caption {
    color: #888;
}

.vb-group-label {
    font-family: "KoddiUDOnGothic-Bold", "KoddiUD OnGothic", sans-serif;
    fill: #fff;
    fill-opacity: 1;
    font-weight: 700;
    stroke-width: 3px;
    pointer-events: none;
}

.vb-cell[data-depth="1"] {
    stroke: #464749aa;
    stroke-width: 1.5;
}

.vb-cell[data-depth="2"] {
    stroke: #46474955;
    stroke-width: 0.7;
}

.vb-cell[data-depth="3"] {
    stroke: #ffffffb0;
    stroke-width: 0.5;
    cursor: pointer;
}

svg.vb-hover-enabled .vb-cell[data-depth="3"]:hover {
    fill: var(--hl, #00000099) !important;
}

.vb-cell[data-depth="3"].vb-clicked {
    stroke-width: 1px;
    filter: hue-rotate(-5deg) brightness(0.9);
}

.vb-subgroup-label {
    font-size: 1.2em;
    font-weight: 600;
    fill: #000d;
    pointer-events: none;
}

.vb-item-label {
    font-size: 0.8em;
    font-weight: 400;
    fill: #a95b5bdd;
    cursor: default;
    pointer-events: none;
}

.vb-item-value {
    fill: #c25a50;
    font-size: 12px;
    cursor: default;
    pointer-events: none;
}

.vb-popup-content {
    position: absolute;
    background: #fffe;
    border: 2px solid #555;
    border-radius: 30px;
    padding: 10px;
    z-index: 1000000;
    transform: translateX(-50%) translateY(-100%);
    min-width: 100px;
}

.vb-popup-content::before {
    content: " ";
    position: absolute;
    top: 100%;
    left: 50%;
    margin-left: -10px;
    border-width: 10px;
    border-style: solid;
    border-color: #555 transparent transparent transparent;
}

.vb-popup-content::after {
    content: " ";
    position: absolute;
    top: 100%;
    left: 50%;
    margin-left: -8px;
    border-width: 8px;
    border-style: solid;
    border-color: #fff transparent transparent transparent;
}

/* Primary popup (showVoronoiPopup from utils): the JS already positions the
   .vb-popup at the final top-left, so the inner content must NOT
   re-apply the legacy centering transform — otherwise it double-offsets up-left.
   Scoped to .vb-popup so the legacy popup keeps its transform. */
.vb-popup .vb-popup-content {
    position: relative;
    transform: none;
}
/* When placed below the cell, flip the arrow to point up from the top edge. */
.vb-popup.vb-popup-below .vb-popup-content::before {
    top: auto;
    bottom: 100%;
    border-color: transparent transparent #555 transparent;
}
.vb-popup.vb-popup-below .vb-popup-content::after {
    top: auto;
    bottom: 100%;
    border-color: transparent transparent #fff transparent;
}

.vb-popup-message {
    max-width: 350px;
    min-width: 200px;
    padding: 1em;
    line-height: 1.5;
    color: #444;
    text-align: left;
    max-height: 400px;
    overflow-y: scroll;
    overflow-x: clip;
}
`;
}

export default {
  showVoronoiPopup,
  createDOMPopup,
  getPopupStyles,
  getBubbleStyles
};
